/* eslint-disable @typescript-eslint/no-explicit-any */
import { Preferences } from "@capacitor/preferences";
import { apiClient } from "./api";
/**
 * Records a user's activity adherence
 * @param activityData The activity adherence data to record
 * @returns The response from the server
 */
export const recordActivityAdherence = async (
  activityData: any
): Promise<any> => {
  try {
    const response: any = await apiClient.post(
      "/api/activity-goals/adherence",
      activityData
    );
    return response.data;
  } catch (error) {
    console.error("Error recording activity adherence:", error);
    throw error;
  }
};

/**
 * Gets the daily status for a user's activity adherence
 * @param profileId The user's profile ID
 * @returns The daily status data
 */
export const getDailyStatus = async (
  profileId: number,
  date?: string
): Promise<any> => {
  try {
    let url = `/api/activity-goals/adherence/${profileId}`;
    if (date) {
      url += `?date=${date}`;
    }
    const response: any = await apiClient.get(url);
    if (response.status !== 200) {
      throw new Error("Failed to fetch daily status");
    }

    const value: any = JSON.stringify(response.data);
    // await Preferences.set({
    //   key: "dailyStatus",
    //   value: JSON.stringify(response.data),
    // });
    return response.data;
  } catch (error) {
    console.error("Error getting daily status:", error);
    throw error;
  }
};
/** Gets the daily therapies for a user's activity adherence
 * @param profileId The user's profile ID
 * @param date Specific date to get therapies for (optional)
 * @returns The daily therapies data
 */
export const getDailyTherapies = async (
  profileId: number,
  date?: string
): Promise<any> => {
  try {
    let url = `/api/activity-goals/daily/${profileId}`;
    if (date) {
      url += `?date=${date}`;
    }
    const response: any = await apiClient.get(url);
    if (response.status !== 200) {
      throw new Error("Failed to fetch daily status");
    }
    return response.data;
  } catch (error) {
    console.error("Error getting daily status:", error);
    throw error;
  }
};
/** Gets the weekly status for a user's activity adherence
 * @param profileId The user's profile ID
 * @param date Specific date to get the week for (optional)
 * @param startDate Start date for the week (optional)
 * @param endDate End date for the week (optional)
 * @returns The weekly status data
 */
export const getWeeklyStatus = async (
  profileId: number,
  options?: {
    date?: string;
    startDate?: string;
    endDate?: string;
    showPercentage?: boolean;
  }
): Promise<any> => {
  try {
    // Get user's current UTC offset in minutes
    const utcoffset = -new Date().getTimezoneOffset();
    // Get user's current IANA timezone string
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let url = `/api/activity-goals/weekly-adherence/${profileId}`;
    const params: string[] = [];
    if (options?.date) params.push(`date=${options.date}`);
    if (options?.startDate) params.push(`startDate=${options.startDate}`);
    if (options?.endDate) params.push(`endDate=${options.endDate}`);
    if (options?.showPercentage) params.push(`showPercentage=true`);
    params.push(`utcOffset=${utcoffset}`);
    params.push(`timezone=${encodeURIComponent(timezone)}`);
    if (params.length > 0) url += `?${params.join("&")}`;
    const response: any = await apiClient.get(url);
    if (response.status !== 200) {
      throw new Error("Failed to fetch weekly status");
    }
    // Store to Preferences about last fetched date and time
    await Preferences.set({
      key: "lastFetchedWeeklyStatus",
      value: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    console.error("Error getting weekly status:", error);
    throw error;
  }
};
/**
 * Gets the daily activity adherence for a user
 * @param profileId The user's profile ID
 * @returns The daily activity adherence data
 */
export const getDailyActivityAdherence = async (
  profileId: number
): Promise<any> => {
  try {
    const response: any = await apiClient.get(
      `/api/activity-goals/adherence/daily/${profileId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error getting daily activity adherence:", error);
    throw error;
  }
};

/**
 * Gets activity summary for a date range for a user
 * @param profileId The user's profile ID
 * @param startDate Start date for the range (optional)
 * @param endDate End date for the range (optional)
 * @returns Activity summary data for the specified date range
 */
export const getActivitySummaryByDateRange = async (
  profileId: number,
  startDate?: string,
  endDate?: string
): Promise<any> => {
  try {
    let url = `/api/activity-goals/adherence/range/${profileId}`;
    // Get user's local date in en-CA format (YYYY-MM-DD)
    const localDate = new Date().toLocaleDateString("en-CA");
    const params: string[] = [`date=${localDate}`];
    if (startDate) {
      params.push(`startDate=${startDate}`);
    }
    if (endDate) {
      params.push(`endDate=${endDate}`);
    }
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
    const response: any = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error("Error getting activity summary by date range:", error);
    throw error;
  }
};
export const getFetchPressureSummary = async (
  profileId: number,
  startDate?: string,
  endDate?: string
): Promise<any> => {
  try {
    let url = `/api/pressure-summary`;
    // Get user's local date in en-CA format (YYYY-MM-DD)
    const localDate = new Date().toLocaleDateString("en-CA");
    const params: string[] = [`profile_id=${profileId}`, `date=${localDate}`];
    if (startDate) {
      params.push(`startDate=${startDate}`);
    }
    if (endDate) {
      params.push(`endDate=${endDate}`);
    }
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
    const response: any = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error("Error getting pressure summary by date range:", error);
    throw error;
  }
};
// export const getFetchPressureSummary = async (
//   profileId: number,
//   startDate?: string,
//   endDate?: string
// ): Promise<any> => {
//   try {
//     let url = `/api/pressure-summary`;
//     const utcoffset = -new Date().getTimezoneOffset();
//     // Get user's current IANA timezone string
//     const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

//     // Get user's local date in en-CA format (YYYY-MM-DD)
//     const localDate = new Date().toLocaleDateString("en-CA");
//     const params: string[] = [`profile_id=${profileId}`, `date=${localDate}`];
//     params.push(`utcOffset=${utcoffset}`);
//     params.push(`timezone=${encodeURIComponent(timezone)}`);
//     if (startDate) {
//       params.push(`startDate=${new Date(startDate +"T00:00:00").toISOString()}`);
//     }
//     if (endDate) {
//       params.push(`endDate=${new Date(endDate+"T23:59:59").toISOString()}`);
//     }
//     if (params.length > 0) {
//       url += `?${params.join("&")}`;
//     }
//     const response: any = await apiClient.get(url);
//     return response.data;
//   } catch (error) {
//     console.error("Error getting pressure summary by date range:", error);
//     throw error;
//   }
// };
